/**
 * Google Analytics 4 — GDPR/PECR-compliant loader.
 *
 * We don't add the GA snippet inline in index.html (that would fire
 * before the cookie banner is even shown). Instead we lazy-inject the
 * script + gtag config only after the user has actively granted
 * analytics consent — and we tear it down (remove the snippet, clear
 * `_ga*` cookies) if they later revoke.
 *
 * To go live, the site owner just sets `REACT_APP_GA_MEASUREMENT_ID`
 * in `/app/frontend/.env` to the property's G-XXXXXXXXXX ID and
 * redeploys.  Without an ID, this module is a no-op.
 */

const GA_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();
const SCRIPT_ID = 'guardian-ga-loader';

let telClicksBound = false;

export function trackEvent(name, params = {}) {
  if (!GA_ID || typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}

function onTelClick(event) {
  const link = event.target.closest?.('a[href^="tel:"]');
  if (!link) return;
  trackEvent('phone_click', {
    event_category: 'engagement',
    link_url: link.getAttribute('href') || '',
  });
}

function bindTelClicks() {
  if (telClicksBound || typeof document === 'undefined') return;
  document.addEventListener('click', onTelClick);
  telClicksBound = true;
}

function unbindTelClicks() {
  if (!telClicksBound || typeof document === 'undefined') return;
  document.removeEventListener('click', onTelClick);
  telClicksBound = false;
}

function clearGaCookies() {
  if (typeof document === 'undefined') return;
  document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((n) => n.startsWith('_ga') || n.startsWith('_gid'))
    .forEach((name) => {
      const past = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
      const host = window.location.hostname;
      // Try a few common cookie scopes
      document.cookie = `${name}=; ${past}; path=/`;
      document.cookie = `${name}=; ${past}; path=/; domain=${host}`;
      const root = host.split('.').slice(-2).join('.');
      if (root && root !== host) {
        document.cookie = `${name}=; ${past}; path=/; domain=.${root}`;
      }
    });
}

export function loadGoogleAnalytics() {
  if (!GA_ID || typeof window === 'undefined') return;
  if (document.getElementById(SCRIPT_ID)) {
    bindTelClicks();
    return;
  }

  const s = document.createElement('script');
  s.id = SCRIPT_ID;
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  // anonymize_ip is a GA4-friendly default that further limits any
  // personal-data exposure even after consent.
  gtag('config', GA_ID, { anonymize_ip: true });
  bindTelClicks();
}

export function unloadGoogleAnalytics() {
  if (typeof window === 'undefined') return;
  unbindTelClicks();
  const el = document.getElementById(SCRIPT_ID);
  if (el) el.remove();
  clearGaCookies();
  // Stop any further pushes from existing references.
  if (window.gtag) {
    window['ga-disable-' + GA_ID] = true;
  }
}
